import React, { Component } from "react";
import PageHeader from "../../components/PageHeader";
import PropTypes from "prop-types";
import { Link, Navigate } from "react-router-dom";
import {
  Breadcrumbs,
  BreadcrumbsItem,
  Card,
  Grid,
  GridItem
} from "@sainsburys-tech/fable";
import AlertWorkflow from "../AlertWorkflow";
import AlertEnergyChart from "../AlertEnergyChart";
import AlertDetails from "../AlertDetails";
import AlertTimeline from "../AlertTimeline";
import AlertActiveIndicator from "../AlertActiveIndicator";
import AlertWorkOrderForm from "../AlertWorkOrderForm";
import AlertWorkOrderList from "../AlertWorkOrderList";
import objectToQueryString from "../../utils/objectToQueryString";
import mapAlertToTimeline from "../AlertTimeline/mapAlertToTimeline";
import * as DefaultRoutes from "../../utils/defaultRoutes";
import BehindFeatureFlag from "../BehindFeatureFlag";
import * as Features from "../../utils/features";
import DeviceTagList from "../DeviceTagList";
import LoadingWrapper from "../LoadingWrapper";
import * as styles from "./AlertDetailsPage.styles";

const defaultRoute = DefaultRoutes.DEFAULT_ALERT_ROUTE;

const buttonGroupWidth = { xs: "1/1", sm: "1/1", lg: "1/2" };
const fieldWidth = { xs: 3, sm: 3, lg: 2 };

class AlertDetailsPage extends Component {
  state = {
    redirectToList: false,
    storeHover: false,
    sensorHover: false,
    alertsLinkHover: false,
    alertReferenceLinkHover: false
  };

  loadAlert = () => {
    this.props.loadAlert();
  };

  handleStoreHoverEnter = () => {
    this.setState({ storeHover: true });
  };

  handleStoreHoverLeave = () => {
    this.setState({ storeHover: false });
  };

  handleSensorHoverEnter = () => {
    this.setState({ sensorHover: true });
  };

  handleSensorHoverLeave = () => {
    this.setState({ sensorHover: false });
  };

  handleAlertsLinkHoverEnter = () => {
    this.setState({ alertsLinkHover: true });
  };

  handleAlertsLinkHoverLeave = () => {
    this.setState({ alertsLinkHover: false });
  };

  handleAlertReferenceLinkHoverEnter = () => {
    this.setState({ alertReferenceLinkHover: true });
  };

  handleAlertReferenceLinkHoverLeave = () => {
    this.setState({ alertReferenceLinkHover: false });
  };

  handleStatusChanged = (event, status) => {
    this.props.changeStatus(status, () => {
      if (status === "Closed") {
        this.setState({ redirectToList: true });
      } else {
        this.loadAlert();
      }
    });
  };

  handleIncludeDataInBaselineChanged = e => {
    const isChecked = e.target.checked;
    this.props.changeIncludeDataInBaseline(isChecked, () => {
      this.loadAlert();
    });
  };

  handleAlertCommentCreated = () => {
    this.loadAlert();
  };

  handleAlertWorkOrderSaved = () => {
    this.loadAlert();
  };

  mapWorkOrdersForList() {
    let workOrders = [];
    if (!this.props.alert.alertWorkOrders) return workOrders;

    workOrders = this.props.alert.alertWorkOrders.map(workOrder => {
      return {
        workOrderNumber: workOrder.workOrderNumber,
        addedBy: workOrder.user.name,
        description: workOrder.description,
        addedDate: workOrder.created
      };
    });

    return workOrders;
  }

  render() {
    const redirectTo =
      this.props.redirectTo == null
        ? `/${defaultRoute}`
        : `/alerts${objectToQueryString(this.props.redirectTo)}`;

    if (this.state.redirectToList) {
      return <Navigate to={redirectTo} replace />;
    }
    const alert = this.props.alert;
    const workflow = this.props.workflow;

    const redirectToSensor = `/Devices/${
      this.props.alert.device.deviceId
    }?sensorId=${this.props.alert.sensor.sensorId}`;

    return (
      <div>
        <div style={styles.breadcrumbsWrapper}>
            <Breadcrumbs>
              <BreadcrumbsItem>
                <Link 
                  to={redirectTo}
                  style={styles.getAlertsLinkStyle(this.state.alertsLinkHover)}
                  onMouseEnter={this.handleAlertsLinkHoverEnter}
                  onMouseLeave={this.handleAlertsLinkHoverLeave}
                >
                  Alerts
                </Link>
              </BreadcrumbsItem>
              <BreadcrumbsItem>
                <Link 
                  to={{ pathname: `/alerts/${alert.alertReference}` }}
                  style={styles.getAlertReferenceLinkStyle(this.state.alertReferenceLinkHover)}
                  onMouseEnter={this.handleAlertReferenceLinkHoverEnter}
                  onMouseLeave={this.handleAlertReferenceLinkHoverLeave}
                >
                  {" "}
                  {alert.alertReference}
                </Link>
              </BreadcrumbsItem>
          </Breadcrumbs>
        </div>
        

        <PageHeader
          description={alert.alertType.description}
          subtitle={alert.alertReference}
        />
        {this.props.alert.device.tags &&
          this.props.alert.device.tags.length > 0 && (
            <div className="fable:pb-4">
              <DeviceTagList deviceTags={this.props.alert.device.tags} />
            </div>
          )}
        <Card 
          className="drop-shadow" 
          style={styles.cardStyle}
        >
          <Grid cols={3} gap="md">
            <GridItem colSpan={3} className="fable:pb-2">
              <Link 
                to={`/stores/${alert.store.storeId}`}
                style={styles.getStoreLinkStyle(this.state.storeHover)}
                onMouseEnter={this.handleStoreHoverEnter}
                onMouseLeave={this.handleStoreHoverLeave}
              >
                {alert.store.name} ({alert.store.storeId})
              </Link>{" "}
              / <Link 
                to={redirectToSensor}
                style={styles.getSensorLinkStyle(this.state.sensorHover)}
                onMouseEnter={this.handleSensorHoverEnter}
                onMouseLeave={this.handleSensorHoverLeave}
              >
                {alert.sensor.name}
              </Link>
            </GridItem>
            <GridItem colSpan={3} className="fable:pb-2">
              {alert.sensor.name}
            </GridItem>
            <GridItem colSpan={3} className="fable:pb-2">
              <AlertActiveIndicator alert={alert} />
            </GridItem>
            <GridItem colSpan={fieldWidth}>
              <AlertDetails alert={alert} />
            </GridItem>

            {alert.alertType.code !== undefined && (
              <GridItem colSpan={3}>
                <AlertEnergyChart alert={alert} />
              </GridItem>
            )}
          </Grid>
        </Card>
        <div className="fable:pt-2" />
        <LoadingWrapper isLoading={workflow.isLoading}>
          <Card
            style={styles.cardStyle}
          >
            <AlertWorkflow
              buttonGroupFieldwidth={buttonGroupWidth}
              alertStatus={alert.status}
              onStatusChanged={this.handleStatusChanged}
              includeDataInBaseline={alert.includeDataInBaseline}
              onIncludeDataInBaselineChanged={
                this.handleIncludeDataInBaselineChanged
              }
            />
          </Card>
        </LoadingWrapper>
        <BehindFeatureFlag feature={Features.WORK_ORDERS}>
          <AlertWorkOrderForm
            alertId={alert.alertId}
            onAlertWorkOrderSaved={this.handleAlertWorkOrderSaved}
          />
          <Card
            style={styles.cardStyle}
          >
            <AlertWorkOrderList alertWorkOrders={this.mapWorkOrdersForList()} />
          </Card>
          <Card
            style={styles.cardStyle}
          >
            <hr className="fable:mb-0" />
          </Card>
        </BehindFeatureFlag>
        <Card
          style={styles.cardStyle}
        >
          <AlertTimeline
            {...mapAlertToTimeline(alert)}
            alertId={alert.alertId}
            onAlertCommentCreated={this.handleAlertCommentCreated}
          />
        </Card>
      </div>
    );
  }
}

AlertDetailsPage.propTypes = {
  alert: PropTypes.object,
  workflow: PropTypes.object,
  changeStatus: PropTypes.func,
  changeIncludeDataInBaseline: PropTypes.func,
  redirectTo: PropTypes.object,
  loadAlert: PropTypes.func
};

export default AlertDetailsPage;
